import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  Shield, Plus, Clock, CheckCircle2, AlertTriangle,
  Trash2, RefreshCw, Pencil, Timer, BarChart3, FileText
} from 'lucide-react';
import { cn } from '../lib/utils';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';
import { Acronym } from '../components/shared/AcronymTooltip';

const API = process.env.REACT_APP_BACKEND_URL;

const PRIORITY_CONFIG = {
  low: { label: 'Low', color: 'bg-gray-100 text-gray-600' },
  normal: { label: 'Normal', color: 'bg-blue-100 text-blue-700' },
  high: { label: 'High', color: 'bg-orange-100 text-orange-700' },
  urgent: { label: 'Urgent', color: 'bg-red-100 text-red-700' },
  critical: { label: 'Critical', color: 'bg-red-100 text-red-700' },
};

const StatCard = ({ icon: Icon, label, value, color, suffix = '' }) => (
  <Card className="bg-white border-gray-border" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <CardContent className="p-3 sm:p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-gray-medium">{label}</p>
          <p className="text-xl sm:text-2xl font-bold text-text-primary mt-1">{value}{suffix}</p>
        </div>
        <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center", color)}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
    </CardContent>
  </Card>
);

export default function SLAManagementPage() {
  const [policies, setPolicies] = useState([]);
  const [dashboard, setDashboard] = useState({});
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editPolicy, setEditPolicy] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', priority: 'normal', first_response_hours: 4, resolution_hours: 24, escalation_hours: 8, applies_to: 'all_tickets', business_hours_only: true });

  const fetchData = useCallback(async () => {
    try {
      const [p, d] = await Promise.all([
        axios.get(`${API}/api/sla/policies`),
        axios.get(`${API}/api/sla/dashboard`),
      ]);
      setPolicies(p.data.policies || []);
      setDashboard(d.data);
    } catch { toast.error('Failed to load SLAs'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async () => {
    if (!form.name.trim()) { toast.error('Policy name required'); return; }
    try {
      await axios.post(`${API}/api/sla/policies`, form);
      toast.success('SLA policy created!');
      setShowCreate(false);
      setForm({ name: '', description: '', priority: 'normal', first_response_hours: 4, resolution_hours: 24, escalation_hours: 8, applies_to: 'all_tickets', business_hours_only: true });
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const toggleActive = async (id, current) => {
    try {
      await axios.put(`${API}/api/sla/policies/${id}`, { active: !current });
      toast.success(!current ? 'Activated' : 'Deactivated');
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const deletePolicy = async (id) => {
    if (!window.confirm('Delete this policy?')) return;
    try { await axios.delete(`${API}/api/sla/policies/${id}`); toast.success('Deleted'); fetchData(); } catch { toast.error('Failed'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><RefreshCw className="h-6 w-6 animate-spin text-gray-medium" /></div>;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="sla-management-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-text-primary"><Acronym term="SLA" /> Management<PageInfoTooltip pageKey="sla-management" /></h1>
          <p className="text-gray-medium text-sm mt-1">Define and track service level agreements</p>
        </div>
        <Button onClick={() => setShowCreate(true)} data-testid="create-sla-btn"><Plus className="h-4 w-4 mr-1" />New Policy</Button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard icon={Shield} label="Total Policies" value={dashboard.total_policies || 0} color="bg-blue-50 text-blue-600" />
        <StatCard icon={CheckCircle2} label="Active" value={dashboard.active_policies || 0} color="bg-emerald-50 text-emerald-600" />
        <StatCard icon={BarChart3} label="Compliance" value={dashboard.compliance_rate || 100} color="bg-purple-50 text-purple-600" suffix="%" />
        <StatCard icon={AlertTriangle} label="Breached" value={dashboard.breached || 0} color="bg-red-50 text-red-600" />
      </div>

      {/* Compliance Overview */}
      {dashboard.total_tracked > 0 && (
        <Card className="bg-white border-gray-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-6">
              <div className="text-center">
                <p className={cn("text-3xl font-bold", dashboard.compliance_rate >= 90 ? "text-emerald-600" : dashboard.compliance_rate >= 70 ? "text-amber-600" : "text-red-600")}>{dashboard.compliance_rate}%</p>
                <p className="text-xs text-gray-medium mt-1">SLA Compliance</p>
              </div>
              <div className="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden">
                <div className={cn("h-full rounded-full transition-all", dashboard.compliance_rate >= 90 ? "bg-emerald-500" : dashboard.compliance_rate >= 70 ? "bg-amber-500" : "bg-red-500")} style={{ width: `${dashboard.compliance_rate}%` }} />
              </div>
              <div className="text-center">
                <p className="text-lg font-bold text-text-primary">{dashboard.total_tracked - (dashboard.breached || 0)}/{dashboard.total_tracked}</p>
                <p className="text-xs text-gray-medium">Met/Total</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Policies List */}
      <Card className="bg-white border-gray-border">
        <CardHeader className="pb-2"><CardTitle className="text-base">SLA Policies</CardTitle></CardHeader>
        <CardContent>
          {policies.length === 0 ? (
            <div className="text-center py-12 text-gray-medium"><Shield className="h-10 w-10 mx-auto mb-3 opacity-30" /><p>No SLA policies yet</p></div>
          ) : (
            <div className="space-y-3">
              {policies.map(policy => {
                const prCfg = PRIORITY_CONFIG[policy.priority] || PRIORITY_CONFIG.normal;
                return (
                  <div key={policy.id} className={cn("p-4 rounded-lg border transition-colors", policy.active ? "border-gray-border" : "border-gray-200 bg-gray-50/50 opacity-70")} data-testid={`sla-policy-${policy.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="font-medium text-sm text-text-primary">{policy.name}</h4>
                          <Badge className={cn("text-[10px]", prCfg.color)}>{prCfg.label}</Badge>
                          <Badge className={cn("text-[10px]", policy.active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500')}>{policy.active ? 'Active' : 'Inactive'}</Badge>
                          {policy.business_hours_only && <Badge className="text-[10px] bg-blue-50 text-blue-600">Business Hours</Badge>}
                        </div>
                        {policy.description && <p className="text-xs text-gray-medium mt-1">{policy.description}</p>}
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        <Switch checked={policy.active} onCheckedChange={() => toggleActive(policy.id, policy.active)} />
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => deletePolicy(policy.id)}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3 mt-3 pt-3 border-t border-gray-100">
                      <div className="text-center">
                        <div className="flex items-center justify-center gap-1 text-xs text-gray-medium"><Clock className="h-3 w-3" />First Response</div>
                        <p className="text-lg font-semibold text-text-primary mt-0.5">{policy.first_response_hours}h</p>
                      </div>
                      <div className="text-center">
                        <div className="flex items-center justify-center gap-1 text-xs text-gray-medium"><Timer className="h-3 w-3" />Resolution</div>
                        <p className="text-lg font-semibold text-text-primary mt-0.5">{policy.resolution_hours}h</p>
                      </div>
                      <div className="text-center">
                        <div className="flex items-center justify-center gap-1 text-xs text-gray-medium"><AlertTriangle className="h-3 w-3" />Escalation</div>
                        <p className="text-lg font-semibold text-text-primary mt-0.5">{policy.escalation_hours}h</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>New SLA Policy</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Policy Name *</Label><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} data-testid="sla-name" /></div>
            <div><Label>Description</Label><Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2} /></div>
            <div><Label>Priority</Label>
              <Select value={form.priority} onValueChange={v => setForm(f => ({ ...f, priority: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(PRIORITY_CONFIG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>1st Response (h)</Label><Input type="number" min={0} step={0.5} value={form.first_response_hours} onChange={e => setForm(f => ({ ...f, first_response_hours: Number(e.target.value) }))} /></div>
              <div><Label>Resolution (h)</Label><Input type="number" min={0} step={0.5} value={form.resolution_hours} onChange={e => setForm(f => ({ ...f, resolution_hours: Number(e.target.value) }))} /></div>
              <div><Label>Escalation (h)</Label><Input type="number" min={0} step={0.5} value={form.escalation_hours} onChange={e => setForm(f => ({ ...f, escalation_hours: Number(e.target.value) }))} /></div>
            </div>
            <div><Label>Applies To</Label>
              <Select value={form.applies_to} onValueChange={v => setForm(f => ({ ...f, applies_to: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all_tickets">All Tickets</SelectItem>
                  <SelectItem value="high_priority">High Priority Only</SelectItem>
                  <SelectItem value="enterprise">Enterprise Clients</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between"><Label>Business Hours Only</Label><Switch checked={form.business_hours_only} onCheckedChange={v => setForm(f => ({ ...f, business_hours_only: v }))} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button><Button onClick={handleCreate} data-testid="save-sla-btn">Create Policy</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
